import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, X, ImagePlus, Loader2, FileText, Sparkles, Radio } from "lucide-react";
import { openDataUrl } from "@/lib/openDataUrl";
import { api } from "@/lib/api";
import { logError } from "@/lib/log";
import { isPdfMedia, mediaStill, processMediaFiles } from "@/lib/livePhotoMedia";
import { LivePhotoSurface } from "@/components/LivePhotoSurface";
import { LivePhotoCaptureDialog } from "@/components/LivePhotoCaptureDialog";
import { livePhotoUploadAccept } from "@/lib/applePhotoImport";

/**
 * Resize + re-encode a File into a JPEG data URL so any size phone photo works.
 * Scales the longest edge down to `maxEdge` and uses `quality` (0..1).
 */
export async function compressImage(file, { maxEdge = 1600, quality = 0.85 } = {}) {
  // SVG / GIF: pass through (already small / vector)
  if (/^image\/(svg\+xml|gif)$/i.test(file.type)) {
    return await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(file);
    });
  }
  const srcUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = srcUrl;
  });
  const ratio = Math.min(1, maxEdge / Math.max(img.naturalWidth, img.naturalHeight));
  const w = Math.max(1, Math.round(img.naturalWidth * ratio));
  const h = Math.max(1, Math.round(img.naturalHeight * ratio));
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", quality);
}

async function readAsDataUrl(file) {
  return await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

// --- Helpers to replace nested ternaries ---
function PreviewArea({ busy, isPdf, value, label, testId, fit = "cover" }) {
  if (busy) return <Loader2 className="w-6 h-6 text-primary animate-spin" />;
  if (isPdf) {
    return (
      <div className="flex flex-col items-center justify-center text-primary" data-testid={testId ? `${testId}-pdf` : undefined}>
        <FileText className="w-7 h-7" />
        <span className="text-[9px] font-bold mt-0.5 tracking-wider">PDF</span>
      </div>
    );
  }
  if (value) return <LivePhotoSurface value={value} alt={label} className="w-full h-full" imageClassName={fit === "contain" ? "object-contain p-2" : "object-cover"} testId={testId ? `${testId}-img` : "photo-uploader-preview"} />;
  return <ImagePlus className="w-6 h-6 text-muted-foreground" />;
}

function captionText({ disabled, busy, value, isPdf, acceptPdf }) {
  if (disabled) return "Read-only";
  if (busy) return "Uploading…";
  if (value) return isPdf ? "PDF attached — tap to replace" : "Tap photo to replace";
  return acceptPdf ? "Tap to upload a photo or PDF" : "Tap to upload or take a picture";
}

/**
 * Compact photo uploader → data URL.
 * Any source size is accepted; the image is auto-downscaled on-device.
 * Set `acceptPdf` to also accept PDF documents (stored as data URL, no compression).
 */
export function PhotoUploader({ value, onChange, label = "Photo", testId, capture, disabled = false, acceptPdf = false, validateAsTank = false, validateKind = "delivery", validateContext = "", colonyId, sharedAcrossColonies = false, allowLive = true, previewFit = "cover" }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [liveOpen, setLiveOpen] = useState(false);
  const [progress, setProgress] = useState(0);

  const isPdf = isPdfMedia(value);

  const processPickedFiles = async (fileList) => {
    if (disabled) return;
    const files = Array.from(fileList || []);
    const f = files[0];
    if (!f) return;
    const isImageFile = /^image\//i.test(f.type);
    const isPdfFile = /^application\/pdf$/i.test(f.type);
    const hasVideo = allowLive && !acceptPdf && files.some((file) => /^video\//i.test(file.type) || /\.(mov|mp4|webm)$/i.test(file.name));
    if (!isImageFile && !hasVideo && !(acceptPdf && isPdfFile)) {
      toast.error(acceptPdf ? "Please choose an image, Live Photo pair, or PDF file" : "Please choose an image or Live Photo pair");
      return;
    }
    // Hard cap PDFs at 8 MB raw (≈10.6 MB base64) so the customer doc stays small.
    if (isPdfFile && f.size > 8 * 1024 * 1024) {
      toast.error("PDF is larger than 8 MB — please use a smaller file");
      return;
    }
    setBusy(true);
    try {
      let dataUrl;
      if (isPdfFile) dataUrl = await readAsDataUrl(f);
      else {
        const result = await processMediaFiles(files, { max: 1, colonyId, sharedAcrossColonies, onProgress: setProgress });
        dataUrl = result.items[0];
        if (!dataUrl) throw new Error("Select the still image and MOV together when importing an iPhone Live Photo.");
        if (result.ignoredVideos) toast.message("An unpaired motion file was skipped. Select its matching photo too.");
      }

      // AI validation: only for images, and only when caller opted in
      if (validateAsTank && isImageFile) {
        setVerifying(true);
        try {
          const r = await api.post("/photos/validate-tank", {
            photo_data_url: mediaStill(dataUrl),
            kind: validateKind,
            context: validateContext,
          });
          if (r.data && r.data.valid === false) {
            toast.error(`AI rejected this photo: ${r.data.reason || "doesn't look like a tank/delivery photo"}. Please retake.`, { duration: 6000 });
            return; // Do NOT call onChange — leaves the slot empty
          }
        } catch (verr) {
          // Fail open — if validator API is unreachable, allow the photo so we never block work
          logError("Photo validator unreachable, allowing photo:", verr);
        } finally {
          setVerifying(false);
        }
      }

      onChange(dataUrl);
    } catch (err) {
      logError("Photo processing failed:", err);
      toast.error("Couldn't process that file — try a different one");
    } finally {
      setBusy(false);
      setProgress(0);
    }
  };
  const onPick = async (e) => {
    await processPickedFiles(e.target.files);
    e.target.value = "";
  };

  const showBusy = busy || verifying;
  const inputAccept = acceptPdf
    ? "image/*,application/pdf"
    : allowLive && !capture
      ? livePhotoUploadAccept("image/*,video/quicktime,video/mp4,video/webm")
      : "image/*";

  return (
    <div className="flex items-center gap-3" data-testid={testId ? `${testId}-wrap` : undefined}>
      <button type="button" onClick={() => fileRef.current?.click()} disabled={disabled || showBusy} aria-label={`${value ? "Replace" : "Upload"} ${label}`} className={`relative w-20 h-20 flex items-center justify-center border border-dashed border-border bg-secondary/40 rounded-sm overflow-hidden ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:border-primary"} transition-colors flex-shrink-0`} data-testid={testId ? `${testId}-upload-trigger` : "photo-uploader-upload-trigger"}>
        <PreviewArea busy={showBusy} isPdf={isPdf} value={value} label={label} testId={testId} fit={previewFit} />
      </button>
      <input ref={fileRef} type="file" accept={inputAccept} multiple disabled={disabled} {...(capture && !acceptPdf ? { capture } : {})} onChange={onPick} className="hidden" data-testid={testId ? `${testId}-input` : "photo-uploader-input"} />
      <div className="text-xs">
        <div className="font-semibold flex items-center gap-1">
          <Camera className="w-3 h-3" /> {label}
          {validateAsTank && <Sparkles className="w-3 h-3 text-primary" title="AI verified" />}
        </div>
        <div className="text-muted-foreground">
          {verifying ? "AI checking photo…" : busy && progress ? `Uploading motion ${progress}%` : captionText({ disabled, busy, value, isPdf, acceptPdf })}
        </div>
        {value && !showBusy && !disabled && (
          <div className="flex items-center gap-2 mt-1">
            {isPdf && (
              <button type="button" onClick={() => openDataUrl(value)} className="text-primary hover:underline" data-testid={testId ? `${testId}-open` : undefined}>
                Open PDF
              </button>
            )}
            <button type="button" onClick={() => onChange("")} className="text-destructive hover:underline inline-flex items-center gap-1" data-testid={testId ? `${testId}-remove` : undefined}>
              <X className="w-3 h-3" /> Remove
            </button>
          </div>
        )}
        {!disabled && !acceptPdf && allowLive && (
          <button type="button" onClick={() => setLiveOpen(true)} className="mt-1 inline-flex items-center gap-1 text-primary hover:underline" data-testid={testId ? `${testId}-live` : "photo-uploader-live"}><Radio className="w-3 h-3" /> Capture Live Photo</button>
        )}
      </div>
      {allowLive && <LivePhotoCaptureDialog open={liveOpen} onOpenChange={setLiveOpen} onCaptured={processPickedFiles} testId={testId ? `${testId}-live-capture` : "photo-uploader-live-capture"} />}
    </div>
  );
}
